import { useEffect } from "react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "./guide.css";

const q = (sel) => document.querySelector(sel);
// element-anchored step when the target exists, otherwise a centered popover
const step = (sel, title, description, side = "bottom", align = "start") =>
  sel && q(sel)
    ? { element: sel, popover: { title, description, side, align } }
    : { popover: { title, description } };

const overviewSteps = () => [
  { popover: {
    title: "Benvenuto in StorMi Preventivatore 👋",
    description: "Questo strumento crea un preventivo di valet storage e lo trasforma in un messaggio WhatsApp pronto da inviare. Scegli una modalità qui sotto: aprendola ti chiederò se vuoi una guida passo-passo." } },
  step('[data-testid="mode-objects"]', "Per oggetti",
    "Costruisci il preventivo scegliendo gli oggetti dal catalogo, uno per uno."),
  step('[data-testid="mode-area"]', "Per area",
    "Calcola il prezzo partendo da metratura, altezza e riempimento della stanza."),
  step('[data-testid="mode-image"]', "Da immagine",
    "Carica una foto: l'AI riconosce gli oggetti e li marca con numeri. Potrai aggiungerne cliccando sulla foto e rimuoverli con la × sul marker."),
  step('[data-testid="mode-text"]', "Da testo",
    "Incolla la richiesta del cliente: l'AI estrae oggetti e dimensioni automaticamente."),
  step('[data-testid="quotes-search-input"]', "Preventivi salvati",
    "Ogni preventivo si salva da solo. Da qui li cerchi, filtri e riapri con \"Apri\"; nelle schede vedi le miniature delle foto."),
  step('[data-testid="settings-button"]', "Impostazioni",
    "Personalizzi tariffe, sconti, testi dei messaggi, modelli AI e le soglie del Profit-o-metro.", "bottom", "end"),
  step('[data-testid="guide-button"]', "Guida sempre disponibile",
    "Riapri questa guida quando vuoi. Dentro ogni modalità la guida è specifica per quella schermata. Buon lavoro! 🚀", "bottom", "end"),
];

const commonQuoteTail = () => [
  step('[data-testid="ritiro-section"]', "Ritiro a domicilio",
    "Attiva il ritiro e inserisci il CAP o la città: distanza e tempo vengono stimati dall'AI. Puoi rendere il ritiro gratuito per impegni più lunghi.", "top"),
  step('[data-testid="price-gauge"]', "Profit-o-metro",
    "Mostra quanto è redditizio il preventivo per durata (verde = più redditizio per StorMi).", "top"),
  step('[data-testid="box-rental-market"]', "Verifica il mercato",
    "Confronta con box/garage in affitto della zona alla metratura indicata.", "top"),
  step('[data-testid="whatsapp-preview"]', "Anteprima WhatsApp",
    "Qui vedi il messaggio finale. Attiva le sezioni (scatola porta abiti, materiali gratuiti) e inserisci il nome cliente.", "left"),
  step('[data-testid="polish-button"]', "Ottimizza con l'AI",
    "Rende il messaggio più persuasivo mantenendo numeri e prezzi.", "left"),
  step('[data-testid="save-button"]', "Salva",
    "Salva il preventivo: lo ritrovi nei \"Preventivi salvati\".", "left"),
  step('[data-testid="whatsapp-copy-button"]', "Copia",
    "Copia il messaggio già formattato e incollalo su WhatsApp. Fatto! ✅", "left"),
];

const objectsSteps = () => [
  { popover: { title: "Preventivo per oggetti", description: "Ti guido passo-passo: aggiungi gli oggetti, controlla i totali e genera il messaggio." } },
  step('[data-testid="catalog-adder-toggle"]', "1 · Apri il catalogo", "Clicca per aprire il catalogo e aggiungere oggetti."),
  step('[data-testid="catalog-search-input"]', "2 · Cerca un oggetto", "Scrivi il nome (es. \"divano\") e premi il + per aggiungerlo al preventivo."),
  step('[data-testid="add-custom-item"]', "3 · Oggetto personalizzato", "Se un oggetto non è a catalogo, aggiungilo qui e imposta dimensioni e prezzo a mano.", "bottom", "end"),
  step('[data-testid="total-card"]', "4 · Totale mensile", "Qui vedi il totale al mese e il costo al m³. Puoi anche impostare un totale manuale."),
  ...commonQuoteTail(),
];

const areaSteps = () => [
  { popover: { title: "Preventivo per area", description: "Calcoliamo il prezzo dallo spazio necessario. Ti guido nei campi da compilare." } },
  step('[data-testid="area-input"]', "1 · Metratura", "Scrivi qui i metri quadri della stanza/spazio da occupare."),
  step('[data-testid="area-altezza-input"]', "2 · Altezza", "Inserisci l'altezza utile in metri (default 2,2 m)."),
  step('[data-testid="area-riempimento-slider"]', "3 · Riempimento", "Trascina per indicare quanto sarà pieno lo spazio (default 25%)."),
  step('[data-testid="area-tariffa-input"]', "4 · Tariffa €/m³", "Tariffa applicata al volume; puoi modificarla e ripristinarla."),
  step('[data-testid="area-volume"]', "5 · Volume e prezzo", "Volume netto e prezzo mensile calcolati automaticamente."),
  step('[data-testid="box-rental-verify-button"]', "6 · Verifica il mercato", "Confronta con box/garage in affitto nella città indicata."),
  step('[data-testid="area-confirm-button"]', "7 · Conferma", "Premi Conferma per generare il messaggio WhatsApp per il deposito a volume.", "top"),
  step('[data-testid="whatsapp-preview"]', "Anteprima WhatsApp", "Il messaggio usa un'introduzione dedicata all'area e un disclaimer sul prezzo finale a volume reale.", "left"),
];

const imageSteps = () => [
  { popover: { title: "Preventivo da immagine", description: "Carica le foto, lascia analizzare l'AI e correggi ciò che serve." } },
  step('[data-testid="image-dropzone"]', "1 · Carica le foto", "Trascina qui le foto o clicca per selezionarle (max 15)."),
  step('[data-testid="image-analyze-button"]', "2 · Analizza", "Avvia l'analisi AI: gli oggetti verranno marcati con numeri sulle foto."),
  step('[data-testid="image-add-button"]', "3 · Aggiungi selezionati", "Controlla nome, dimensioni e quantità (gli oggetti a bassa confidenza sono in cima) e aggiungili al preventivo.", "top"),
];

const imageDoneSteps = () => [
  { popover: { title: "Foto analizzate", description: "Ora puoi rifinire il preventivo direttamente sulle foto." } },
  step('[data-testid="photo-inventory"]', "Foto con marker", "I numeri sono gli oggetti del preventivo."),
  step('[data-testid="photo-add-mode-toggle"]', "Aggiungi dalla foto", "Attiva questa modalità e clicca su un oggetto nella foto: l'AI lo riconosce e lo aggiunge. Usa la × su un marker per rimuoverlo.", "bottom", "end"),
  ...commonQuoteTail(),
];

const textSteps = () => [
  { popover: { title: "Preventivo da testo", description: "Incolla la richiesta del cliente e lascia estrarre gli oggetti all'AI." } },
  step('[data-testid="text-input"]', "1 · Incolla il testo", "Incolla qui il messaggio del cliente (oggetti, dimensioni, zona/CAP)."),
  step('[data-testid="text-analyze-button"]', "2 · Analizza con l'AI", "L'AI estrae oggetti e dimensioni; potrai correggerli prima di aggiungerli."),
  step('[data-testid="text-add-button"]', "3 · Aggiungi al preventivo", "Aggiungi gli oggetti estratti: si aprirà il preventivo completo.", "top"),
  step('[data-testid="adapt-why-button"]', "AI · Adatta i vantaggi", "In modalità testo puoi riscrivere \"Perché scegliere StorMi\" in base alla richiesta del cliente.", "left"),
  ...commonQuoteTail(),
];

const settingsSteps = () => [
  { popover: { title: "Impostazioni", description: "Qui personalizzi tutto il comportamento del preventivatore. Usa la barra di navigazione per spostarti tra le sezioni." } },
  step('[data-testid="settings-nav-prezzi"]', "Prezzi e tariffe", "Tariffa €/m³, tariffa oraria, riempimento default dell'area e altri parametri di base.", "bottom"),
  step('[data-testid="settings-nav-testi"]', "Testi del messaggio", "Saluto, introduzione (anche quella dedicata all'Area), disclaimer, materiali e i vantaggi \"Perché scegliere StorMi\".", "bottom"),
  step('[data-testid="settings-nav-profitometro"]', "Profit-o-metro", "Soglie e periodi che colorano il Profit-o-metro.", "bottom"),
  step('[data-testid="settings-nav-modelli-ai"]', "Modelli AI", "Scegli quale modello AI usare per ogni funzione.", "bottom"),
  step('[data-testid="settings-save-button"]', "Salva", "Ricordati di salvare le modifiche.", "bottom", "end"),
];

const buildSteps = ({ mode, settingsOpen }) => {
  if (settingsOpen) return settingsSteps();
  if (mode === null || mode === undefined) return overviewSteps();
  if (mode === "area" || mode === "area_done") return areaSteps();
  if (mode === "image") return imageSteps();
  if (mode === "image_done") return imageDoneSteps();
  if (mode === "text") return textSteps();
  if (mode === "text_done") return textSteps();
  return objectsSteps();
};

export default function GuideDialog({ open, onOpenChange, mode, settingsOpen }) {
  useEffect(() => {
    if (!open) return;
    const steps = buildSteps({ mode, settingsOpen });
    let done = false;
    const finish = () => { if (done) return; done = true; onOpenChange(false); };
    const drv = driver({
      showProgress: true,
      allowClose: true,
      overlayColor: "rgba(15, 23, 42, 0.6)",
      stagePadding: 6,
      stageRadius: 8,
      popoverClass: "stormi-guide",
      nextBtnText: "Avanti →",
      prevBtnText: "← Indietro",
      doneBtnText: "Ho capito",
      progressText: "{{current}} / {{total}}",
      steps,
      // onDestroyStarted fires reliably on close-btn / ESC / overlay / done,
      // even during the opening animation (unlike onDestroyed), so sync state here
      onDestroyStarted: () => { finish(); try { drv.destroy(); } catch { /* noop */ } },
      onDestroyed: finish,
    });
    drv.drive();
    return () => { done = true; try { drv.destroy(); } catch { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return null;
}
